import { LRUCache } from 'lru-cache';

export type L1Stats = {
  enabled: true;
  hits: number;
  misses: number;
  sets: number;
  invalidations: number;
  evictions: number;
  staleHits: number;
  size: number;
  ipcAvoided: number;
};

type L1Envelope<V> = {
  value: V;
  version: number;
  key: unknown;
};

type L1EmitEvent = 'hit' | 'miss' | 'set' | 'invalidate' | 'evict' | 'stale-hit';

type L1EmitPayload = {
  key?: unknown;
  version?: number;
  reason?: string;
};

type L1EmitFn = (event: L1EmitEvent, payload: L1EmitPayload) => void;

type L1ConstructorOptions = {
  max?: number;
  maxSize?: number;
  ttl?: number;
  updateAgeOnGet?: boolean;
  allowStale?: boolean;
  emit?: L1EmitFn; // optional; defaults to no-op
};

const objectKeyIds = new WeakMap<object, number>();
let nextObjectKeyId = 0;

function objectKeyId(key: object): number {
  let id = objectKeyIds.get(key);
  if (id === undefined) {
    id = ++nextObjectKeyId;
    objectKeyIds.set(key, id);
  }
  return id;
}

// Encode arbitrary cache keys into a string the L1 LRUCache can index by.
// Primitives use a typed prefix so 1 (number) and "1" (string) do not collide.
// Objects and functions use WeakMap-backed identity IDs so L1 mirrors
// lru-cache's SameValueZero key semantics instead of structural equality.
// Symbol keys are rejected because Symbol.toString is not stable across realms
// and the L1 would dedup by description, which is wrong.
//
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export function encodeL1Key(key: {}): string {
  switch (typeof key) {
    case 'string':
      return `s:${key}`;
    case 'number':
      return `n:${key}`;
    case 'boolean':
      return `b:${key}`;
    case 'bigint':
      return `i:${key.toString()}`;
    case 'symbol':
      throw new Error('L1 does not support symbol keys');
    case 'function':
      return `f:${objectKeyId(key)}`;
    default:
      return `o:${objectKeyId(key)}`;
  }
}

const FRESH_STATS = (): L1Stats => ({
  enabled: true,
  hits: 0,
  misses: 0,
  sets: 0,
  invalidations: 0,
  evictions: 0,
  staleHits: 0,
  size: 0,
  ipcAvoided: 0,
});

// Per-instance L1 cache. One LocalL1Cache per LRUCacheClustered.
// Stores L1Envelope<V> so we can stamp each entry with the namespace version
// at the moment it was populated; later reads compare against the latest
// invalidation version we've observed for the namespace.
//
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export class LocalL1Cache<V extends {} = {}> {
  readonly #cache: LRUCache<string, L1Envelope<V>>;
  readonly #ttl?: number;
  #latestSeen = 0;
  readonly #stats = FRESH_STATS();
  readonly #emit: L1EmitFn;

  constructor(opts: L1ConstructorOptions) {
    this.#emit = opts.emit ?? (() => undefined);
    this.#ttl = opts.ttl;
    const lruOpts = {
      max: opts.max ?? 1000,
      ttl: opts.ttl,
      updateAgeOnGet: opts.updateAgeOnGet ?? true,
      allowStale: opts.allowStale ?? false,
      maxSize: opts.maxSize,
      // sizeCalculation is required when maxSize is set, but for v1 we don't
      // expose it in the public LocalL1Options. If a caller passes maxSize,
      // we use a 1-per-entry calculation as a placeholder.
      ...(opts.maxSize !== undefined ? { sizeCalculation: () => 1 } : {}),
      dispose: (value: L1Envelope<V>, key: string, reason: LRUCache.DisposeReason) => {
        if (reason === 'evict') {
          this.#stats.evictions += 1;
          this.#emit('evict', { key: value.key ?? key, reason: 'lru' });
        }
      },
    } as ConstructorParameters<typeof LRUCache<string, L1Envelope<V>>>[0];
    this.#cache = new LRUCache(lruOpts);
  }

  get(encodedKey: string, eventKey: unknown = encodedKey): V | undefined {
    const status: { returnedStale?: true } = {};
    const entry = this.#cache.get(encodedKey, { status });
    if (!entry) {
      this.#stats.misses += 1;
      this.#emit('miss', { key: eventKey });
      return undefined;
    }
    if (status.returnedStale) {
      this.#stats.staleHits += 1;
      this.#emit('stale-hit', { key: entry.key });
    }
    if (entry.version < this.#latestSeen) {
      // Stamped before the latest invalidation; drop it.
      this.#cache.delete(encodedKey);
      this.#stats.misses += 1;
      this.#stats.staleHits += 1;
      this.#emit('stale-hit', { key: entry.key });
      this.#emit('miss', { key: eventKey });
      return undefined;
    }
    this.#stats.hits += 1;
    this.#stats.ipcAvoided += 1;
    this.#emit('hit', { key: entry.key });
    return entry.value;
  }

  set(encodedKey: string, value: V, version: number, ttl?: number, eventKey: unknown = encodedKey): void {
    if (version < this.#latestSeen) {
      // Don't store an entry already known to be stale.
      return;
    }
    const setOpts = this.#setOptions(ttl);
    if (setOpts === false) return;
    this.#cache.set(encodedKey, { value, version, key: eventKey }, setOpts);
    this.#stats.sets += 1;
    this.#emit('set', { key: eventKey, version });
  }

  delete(encodedKey: string, eventKey: unknown = encodedKey, emit = true): void {
    if (this.#cache.delete(encodedKey)) {
      this.#stats.invalidations += 1;
      if (emit) this.#emit('invalidate', { key: eventKey, reason: 'self' });
    }
  }

  clear(emit = true): void {
    if (this.#cache.size > 0) this.#stats.invalidations += 1;
    this.#cache.clear();
    if (emit) this.#emit('invalidate', { key: '*', reason: 'clear' });
  }

  advanceLatestSeen(version: number): void {
    if (version > this.#latestSeen) this.#latestSeen = version;
  }

  latestSeen(): number {
    return this.#latestSeen;
  }

  stats(): L1Stats {
    return { ...this.#stats, size: this.#cache.size };
  }

  // For tests / explicit teardown.
  destroy(): void {
    this.#cache.clear();
  }

  #setOptions(ttl: number | undefined): { ttl: number } | undefined | false {
    if (ttl === undefined || !Number.isFinite(ttl)) return undefined;
    const capped = this.#ttl === undefined ? ttl : Math.min(ttl, this.#ttl);
    return capped > 0 ? { ttl: capped } : false;
  }
}
